import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import "./BookAppointment.css";
import { toast } from "react-toastify";
import { FaEdit, FaEye, FaPlus, FaTrashAlt, FaTimes, FaChevronDown } from "react-icons/fa";
import CustomDatePicker from "../../resuableComponents/CustomDatePicker";
import CustomTimePicker from "../../resuableComponents/CustomTimePicker";
import CustomDropdown from "../../resuableComponents/CustomDropdown";
import dayjs from "dayjs";
import SkeletonLoader from "../../resuableComponents/SkeletonLoader";
import { handleApiError } from "../../../utils/errorHandler";

const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const itemsAddedOnVisit = (pkg, visitId) =>
  (pkg?.items || []).filter(
    (item) => visitId && String(item.addedOnVisitId || "") === String(visitId)
  );

const itemsFromEarlierVisits = (pkg, visitId) =>
  (pkg?.items || []).filter(
    (item) => !visitId || String(item.addedOnVisitId || "") !== String(visitId)
  );

const resolvePackageItems = (savedItems, treatmentOptions) => {
  const usedIds = new Set();
  return (savedItems || []).map((item) => {
    const rawName = typeof item === "string" ? item : item?.name;
    const serviceId = typeof item === "object" ? item?.serviceId : undefined;
    const byId = serviceId
      ? treatmentOptions.find(
          (opt) => sameId(opt._id, serviceId) && !usedIds.has(String(opt._id))
        )
      : null;
    const match =
      byId ||
      treatmentOptions.find(
        (opt) => opt.name === rawName && !usedIds.has(String(opt._id))
      );
    if (match) {
      usedIds.add(String(match._id));
      return { _id: match._id, name: match.name, cost: match.cost };
    }
    const fallbackId = serviceId || rawName;
    usedIds.add(String(fallbackId));
    return { _id: fallbackId, name: rawName, cost: 0 };
  });
};

const BookAppointment = ({ patientId }) => {
  const [appointments, setAppointments] = useState([]);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [patientType, setPatientType] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [notes, setNotes] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [appointmentsLoading, setAppointmentsLoading] = useState(false);
  const [savingAppointment, setSavingAppointment] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showConsultationModal, setShowConsultationModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [modalMode, setModalMode] = useState("add"); // 'add' or 'view'
  const [treatmentSearch, setTreatmentSearch] = useState("");
  const [medicineSearch, setMedicineSearch] = useState("");
  const treatmentDropdownRef = useRef(null);
  const medicineDropdownRef = useRef(null);

  // Consultation form states
  const [bp, setBp] = useState("");
  const [bsl, setBsl] = useState("");
  const [weight, setWeight] = useState("");
  const [temperature, setTemperature] = useState("");
  const [pulse, setPulse] = useState("");

  const [treatments, setTreatments] = useState([]);
  const [appointmentPackages, setAppointmentPackages] = useState([]);
  const [packageMode, setPackageMode] = useState(false);
  const [packageItems, setPackageItems] = useState([]);
  const [masterId, setMasterId] = useState("");
  const [packageDuration, setPackageDuration] = useState("single");
  const [packageKind, setPackageKind] = useState("standard");
  const [customPackageName, setCustomPackageName] = useState("");
  const [customPackageDays, setCustomPackageDays] = useState("");
  const [customPackagePrice, setCustomPackagePrice] = useState("");
  const [continuePackage, setContinuePackage] = useState(false);
  const [packageLocked, setPackageLocked] = useState(false);
  const [medicineName, setMedicineName] = useState("");
  const [followUpDate, setFollowUpDate] = useState(null);
  const [consultationLoading, setConsultationLoading] = useState(false);

  // New visit-aware states
  const [visitId, setVisitId] = useState("");
  const [visitDate, setVisitDate] = useState(null);
  const [visitTime, setVisitTime] = useState(null);
  const [complaints, setComplaints] = useState("");
  const [selectedVisits, setSelectedVisits] = useState([]);

  // Observations fields
  const [co, setCo] = useState(""); // Chief Complaint
  const [ho, setHo] = useState(""); // History of Presenting Illness
  const [kco, setKco] = useState(""); // Known Case Of
  const [sh, setSh] = useState(""); // Social History
  const [dh, setDh] = useState(""); // Dietary History
  const [addictions, setAddictions] = useState(""); // Addictions
  const [advices, setAdvices] = useState(""); // Advices
  const [spo2, setSpo2] = useState(""); // SpO2
  const [height, setHeight] = useState(""); // Height

  // Systemic Examination fields
  const [cvs, setCvs] = useState(""); // Cardiovascular System
  const [cns, setCns] = useState(""); // Central Nervous System
  const [rs, setRs] = useState(""); // Respiratory System
  const [pa, setPa] = useState(""); // Per Abdomen

  // Ayurvedic Examination fields
  const [mala, setMala] = useState(""); // Bowel habits
  const [mutra, setMutra] = useState(""); // Urine characteristics
  const [jivha, setJivha] = useState(""); // Tongue
  const [nidra, setNidra] = useState(""); // Sleep

  // Menstrual History fields
  const [menarche, setMenarche] = useState("");
  const [lmp, setLmp] = useState(null);
  const [bleedingDuration, setBleedingDuration] = useState("");
  const [cycleDuration, setCycleDuration] = useState("");
  const [cycleRegularity, setCycleRegularity] = useState("");
  const [flow, setFlow] = useState("");
  const [clots, setClots] = useState("");
  const [dysmenorrhea, setDysmenorrhea] = useState("");
  const [menopause, setMenopause] = useState("");

  const authData = JSON.parse(localStorage.getItem("authData"));
  const doctorId = authData?.user?._id;
  const token = authData?.accessToken;
  const [treatmentOptions, setTreatmentOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  //declaration for meicines
  const [medicines, setMedicines] = useState([]);
  const [medicineOptions, setMedicineOptions] = useState([]);
  const [isMedicineLoading, setIsMedicineLoading] = useState(false);
  const [medicineError, setMedicineError] = useState(null);
  const [isMedicineDropdownOpen, setIsMedicineDropdownOpen] = useState(false);
  const [dosageInputs, setDosageInputs] = useState({});

  // const treatmentOptions = [
  //   "Enema",
  //   "Vomiting",
  //   "Laxative",
  //   "Oil Massage",
  //   "Steam bath",
  // ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        treatmentDropdownRef.current &&
        !treatmentDropdownRef.current.contains(event.target)
      ) {
        setIsDropdownOpen(false);
      }

      if (
        medicineDropdownRef.current &&
        !medicineDropdownRef.current.contains(event.target)
      ) {
        setShowMedicineDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const generateTimeSlots = () => {
    const slots = [];
    let start = 7 * 60; // 8:00 AM
    let end = 19 * 60; // 8:00 PM
    while (start <= end) {
      const hours = Math.floor(start / 60);
      const minutes = start % 60;
      const timeStr = `${hours.toString().padStart(2, "0")}:${minutes
        .toString()
        .padStart(2, "0")}`;
      slots.push(timeStr);
      start += 30; // 30-minute intervals
    }
    return slots;
  };

  const getTodayIST = () => {
    // Get current date in IST timezone
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const istTime = new Date(now.getTime() + istOffset);
    return dayjs(istTime).startOf("day");
  };

  const fetchAppointments = async () => {
    try {
      setAppointmentsLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}appointments/${patientId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAppointments(res.data.appointments || []);
    } catch (err) {
      console.error("Failed to fetch appointments:", err);
    } finally {
      setAppointmentsLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
  }, []);

  // Add click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".ba-medicine-search-container")) {
        setShowMedicineDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const formatDate = (isoDate) => {
    const d = new Date(isoDate);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleSaveAppointment = async () => {
    if (!date || !time) {
      toast.error("Please fill all the required fields.");
      return;
    }

    const formattedDate = dayjs(date).format("DD-MM-YYYY");
    const formattedTime = dayjs(time).format("HH:mm");

    const payload = {
      patientId,
      doctorId,
      date: formattedDate,
      time: formattedTime,
      notes: diagnosis,
    };

    try {
      setSavingAppointment(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}book-appointment`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setDate(null);
      setTime(null);
      setDiagnosis("");
      setShowForm(false);
      fetchAppointments();
      toast.success("Appointment booked successfully!");
    } catch (err) {
      console.error("Failed to book appointment:", err);
      handleApiError(err, "Failed to book appointment");
    } finally {
      setSavingAppointment(false);
    }
  };

  const handleAddConsultation = (appointment) => {
    setSelectedAppointment(appointment);
    setModalMode("add");
    setVisitDate(dayjs());
    setVisitTime(dayjs());
    resetConsultationForm();
    setShowConsultationModal(true);
  };

  const handleManageVisits = async (appointment) => {
    if (!appointment._id) return;

    setSelectedAppointment(appointment);
    setModalMode("visits");
    setConsultationLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}consultations/${appointment._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const consultationData = response.data.consultation;
      const visits = consultationData?.visits || [];
      setSelectedVisits(visits);
      setAppointmentPackages(consultationData?.packages || []);
      setPatientType(consultationData?.patientType || "");
      setShowConsultationModal(true);
    } catch (error) {
      console.error("Error fetching visits:", error);
      // Treat all errors as empty consultation history (like IPD list)
      // Don't show error toast for empty cases
      setSelectedVisits([]);
      setAppointmentPackages([]);
      setPatientType("");
      setShowConsultationModal(true);
    } finally {
      setConsultationLoading(false);
    }
  };

  const handleEditVisit = (visit) => {
    setVisitId(visit._id);
    setVisitDate(dayjs(visit.date));
    setVisitTime(visit.time ? dayjs(`2000-01-01T${visit.time}`) : null);
    setComplaints(visit.complaints || "");

    // Objective Examination fields
    setBp(visit.observations?.oe?.bp || visit.observations?.bp || "");
    setBsl(visit.observations?.bsl || "");
    setWeight(visit.observations?.oe?.weight || visit.observations?.weight || "");
    setTemperature(visit.observations?.oe?.temp || visit.observations?.temperature || "");
    setPulse(visit.observations?.oe?.pulse || visit.observations?.pulse || "");
    setHeight(visit.observations?.oe?.height || "");
    setSpo2(visit.observations?.oe?.spo2 || "");

    // Other observation fields
    setCo(visit.observations?.co || "");
    setHo(visit.observations?.ho || "");
    setKco(visit.observations?.kco || "");
    setSh(visit.observations?.sh || "");
    setDh(visit.observations?.dh || "");
    setAddictions(visit.observations?.addictions || "");

    // Systemic Examination
    setCvs(visit.observations?.se?.cvs || "");
    setCns(visit.observations?.se?.cns || "");
    setRs(visit.observations?.se?.rs || "");
    setPa(visit.observations?.se?.pa || "");

    // Ayurvedic Examination
    setMala(visit.observations?.examination?.mala || "");
    setMutra(visit.observations?.examination?.mutra || "");
    setJivha(visit.observations?.examination?.jivha || "");
    setNidra(visit.observations?.examination?.nidra || "");

    // Menstrual History
    const mh = visit.observations?.menstrualHistory;
    setMenarche(mh?.menarche || "");
    setLmp(mh?.lmp ? dayjs(mh.lmp) : null);
    setBleedingDuration(mh?.bleedingDuration || "");
    setCycleDuration(mh?.cycleDuration || "");
    setCycleRegularity(mh?.cycleRegularity || "");
    setFlow(mh?.flow || "");
    setClots(mh?.clots || "");
    setDysmenorrhea(mh?.dysmenorrhea || "");
    setMenopause(mh?.menopause || "");

    setDiagnosis(visit.diagnosis || "");
    setNotes(visit.notes || "");
    setAdvices(visit.advices || "");
    setTreatments(
      (visit.treatments || [])
        .filter((treatment) => !(treatment && typeof treatment === "object" && treatment.inPackage))
        .map((treatment) => {
        const treatmentName = typeof treatment === 'string' ? treatment : treatment.name;
        const treatmentCost = typeof treatment === 'object' ? treatment.cost : undefined;
        const match = treatmentOptions.find(
          (opt) => opt.name === treatmentName && Number(opt.cost) === Number(treatmentCost)
        ) || treatmentOptions.find((opt) => opt.name === treatmentName);
        return match ? { _id: match._id, name: match.name, cost: match.cost } : { name: treatmentName, cost: treatment.cost || 0 };
      })
    );
    const ownedPackage = appointmentPackages.find(
      (pkg) => String(pkg.billingVisitId) === String(visit._id)
    );
    if (ownedPackage) {
      setPackageMode(true);
      setPackageLocked(false);
      setContinuePackage(false);
      setPackageDuration(ownedPackage.duration === "multi" ? "multi" : "single");
      setPackageKind(ownedPackage.kind === "custom" ? "custom" : "standard");
      setCustomPackageName(ownedPackage.packageName || "");
      setCustomPackageDays(ownedPackage.days ? String(ownedPackage.days) : "");
      setCustomPackagePrice(
        ownedPackage.price === 0 || ownedPackage.price
          ? String(ownedPackage.price)
          : ""
      );
      const items = resolvePackageItems(ownedPackage.items, treatmentOptions);
      setPackageItems(items);
      const master = ownedPackage.masterServiceId
        ? items.find((item) => sameId(item._id, ownedPackage.masterServiceId))
        : items.find((item) => item.name === ownedPackage.masterName);
      setMasterId(master?._id || "");
    } else if (appointmentPackages.length > 0) {
      const existingPackage = appointmentPackages[0];
      const continuing = existingPackage.duration === "multi";
      setPackageMode(false);
      setPackageLocked(true);
      setContinuePackage(continuing);
      setPackageKind(existingPackage.kind === "custom" ? "custom" : "standard");
      setCustomPackageName("");
      setCustomPackageDays("");
      setCustomPackagePrice("");
      setPackageItems(
        continuing
          ? resolvePackageItems(itemsAddedOnVisit(existingPackage, visit._id), treatmentOptions)
          : []
      );
      setMasterId("");
    } else {
      setPackageMode(false);
      setPackageLocked(false);
      setContinuePackage(false);
      setPackageKind("standard");
      setCustomPackageName("");
      setCustomPackageDays("");
      setCustomPackagePrice("");
      setPackageItems([]);
      setMasterId("");
    }
    setMedicines(
      visit.prescribedMedicines?.map((med) => ({
        ...med,
        _id: med.medicineId || med._id,
        dosage: med.dosage || ''
      })) || []
    );
    setFollowUpDate(visit.followUpDate ? dayjs(visit.followUpDate.split("T")[0]) : null);
    setModalMode("edit");
  };

  const resetConsultationForm = () => {
    setVisitId("");
    setVisitDate(dayjs());
    setVisitTime(dayjs());
    setComplaints("");
    setBp("");
    setBsl("");
    setWeight("");
    setTemperature("");
    setPulse("");
    setDiagnosis("");
    setNotes("");
    setAdvices("");
    setTreatments([]);
    setPackageMode(false);
    setPackageItems([]);
    setMasterId("");
    setPackageDuration("single");
    setPackageKind("standard");
    setCustomPackageName("");
    setCustomPackageDays("");
    setCustomPackagePrice("");
    setContinuePackage(false);
    setPackageLocked(false);
    setMedicines([]);
    setMedicineName("");
    setMedicineDosage("");
    setFollowUpDate(null);

    // Reset observations fields
    setCo("");
    setHo("");
    setKco("");
    setSh("");
    setDh("");
    setAddictions("");
    setSpo2("");
    setHeight("");

    // Reset systemic examination
    setCvs("");
    setCns("");
    setRs("");
    setPa("");

    // Reset ayurvedic examination
    setMala("");
    setMutra("");
    setJivha("");
    setNidra("");

    // Reset menstrual history
    setMenarche("");
    setLmp(null);
    setBleedingDuration("");
    setCycleDuration("");
    setCycleRegularity("");
    setFlow("");
    setClots("");
    setDysmenorrhea("");
    setMenopause("");
  };

  const handleTreatmentToggle = (treatment) => {
    if (packageItems.some((item) => sameId(item._id, treatment._id))) {
      const next = packageItems.filter((item) => !sameId(item._id, treatment._id));
      setPackageItems(next);
      if (sameId(masterId, treatment._id)) setMasterId(next[0]?._id || "");
    }
    const exists = treatments.some((item) =>
      item._id && treatment._id
        ? sameId(item._id, treatment._id)
        : item.name === treatment.name
    );
    if (exists) {
      setTreatments(treatments.filter((item) =>
        item._id && treatment._id
          ? !sameId(item._id, treatment._id)
          : item.name !== treatment.name
      ));
    } else {
      setTreatments([...treatments, { _id: treatment._id, name: treatment.name, cost: treatment.cost }]);
    }
  };

  const togglePackageItem = (treatment) => {
    if (packageItems.some((item) => sameId(item._id, treatment._id))) {
      const next = packageItems.filter((item) => !sameId(item._id, treatment._id));
      setPackageItems(next);
      setMasterId((currentMaster) =>
        sameId(currentMaster, treatment._id) ? next[0]?._id || "" : currentMaster
      );
      return;
    }
    setTreatments((prev) => prev.filter((item) => !sameId(item._id, treatment._id)));
    setPackageItems((prev) => [...prev, { _id: treatment._id, name: treatment.name, cost: treatment.cost }]);
    setMasterId((currentMaster) => currentMaster || treatment._id);
  };

  const hasConsultationContent = () =>
    Boolean(
      bp ||
      bsl ||
      weight ||
      temperature ||
      pulse ||
      complaints ||
      diagnosis ||
      notes ||
      followUpDate ||
      treatments.length > 0 ||
      medicines.length > 0 ||
      (packageMode && packageItems.length > 0) ||
      (packageMode && packageKind === "custom" && (customPackageName || customPackageDays || customPackagePrice)) ||
      continuePackage
    );

  const resolvePackageMasterId = () => {
    if (!packageMode || packageItems.length === 0) return "";
    if (packageItems.some((item) => sameId(item._id, masterId))) return masterId;
    return packageItems[0]._id || "";
  };

  const validatePackage = () => {
    if (!packageMode) return true;
    if (packageKind === "custom") {
      const days = Number(customPackageDays);
      const price = Number(customPackagePrice);
      if (!customPackageName.trim()) {
        toast.error("Enter the custom package name.");
        return false;
      }
      if (!Number.isInteger(days) || days < 1) {
        toast.error("Enter the number of days for the custom package.");
        return false;
      }
      if (customPackagePrice === "" || Number.isNaN(price) || price < 0) {
        toast.error("Enter the custom package price.");
        return false;
      }
      return true;
    }
    if (packageItems.length < 1) {
      toast.error("A standard package needs at least one treatment.");
      return false;
    }
    return true;
  };

  const buildPackageFields = (resolvedMasterId = masterId) => {
    if (packageMode && packageKind === "custom") {
      const days = Number(customPackageDays);
      return {
        treatmentPackage: {
          kind: "custom",
          packageName: customPackageName.trim(),
          days,
          price: Number(customPackagePrice),
          duration: days > 1 ? "multi" : "single",
          items: packageItems.map((item) => ({
            name: item.name,
            serviceId: item._id,
          })),
        },
      };
    }
    if (packageMode) {
      const master =
        packageItems.find((item) => sameId(item._id, resolvedMasterId)) ||
        packageItems[0];
      return {
        treatmentPackage: {
          duration: packageDuration,
          masterName: master?.name || "",
          masterServiceId: master?._id || "",
          items: packageItems.map((item) => ({
            name: item.name,
            serviceId: item._id,
          })),
        },
      };
    }
    if (continuePackage) {
      return {
        continuePackage: true,
        packageAdditions: packageItems.map((item) => ({
          name: item.name,
          serviceId: item._id,
        })),
      };
    }
    const ownedPackage = appointmentPackages.find(
      (pkg) => String(pkg.billingVisitId) === String(visitId)
    );
    if (modalMode === "edit" && ownedPackage) {
      return { treatmentPackage: null };
    }
    return {};
  };

  const billableTreatmentNames = () => {
    const blocked = new Set(packageItems.map((item) => item.name));
    if (continuePackage) {
      const pkg = appointmentPackages.find((item) => item.duration === "multi");
      (pkg?.items || []).forEach((item) => {
        if (item?.name) blocked.add(item.name);
      });
    }
    return treatments
      .filter((item) => !blocked.has(item.name))
      .map((item) => item.name);
  };

  // const addMedicine = () => {
  //   if (!medicineId || !medicineDosage) return;

  //   const selected = availableMedicines.find((m) => m._id === medicineId);
  //   if (!selected) return;

  //   setMedicines((prev) => [
  //     ...prev,
  //     {
  //       name: selected.name,
  //       dosage: Number(medicineDosage),
  //       cost: selected.cost,
  //     },
  //   ]);

  //   setMedicineId("");
  //   setMedicineDosage("");
  // };

  const handleSaveConsultation = async () => {
    if (!patientType) {
      toast.error("Patient type is required.");
      return;
    }

    if (!hasConsultationContent()) {
      toast.error("At least one field must be filled before saving.");
      return;
    }

    if (!validatePackage()) return;

    const formatFollowUpDate = (dateValue) => {
      if (!dateValue) return "";
      return dayjs(dateValue).format("DD-MM-YYYY");
    };

    // Check if menstrual history has any data
    const hasMenstrualData = menarche || lmp || bleedingDuration || cycleDuration || cycleRegularity || flow || clots || dysmenorrhea || menopause;

    const observations = {
      bsl,
      co,
      ho,
      kco,
      sh,
      dh,
      addictions,
      oe: {
        bp,
        pulse,
        temp: temperature,
        weight,
        height,
        spo2,
      },
      se: {
        cvs,
        cns,
        rs,
        pa,
      },
      examination: {
        mala,
        mutra,
        jivha,
        nidra,
      },
    };

    // Only include menstrualHistory if at least one field has data
    if (hasMenstrualData) {
      observations.menstrualHistory = {
        menarche,
        lmp: lmp ? dayjs(lmp).format("DD-MM-YYYY") : "",
        bleedingDuration,
        cycleDuration,
        cycleRegularity,
        flow,
        clots,
        dysmenorrhea,
        menopause,
      };
    }

    const resolvedMasterId = resolvePackageMasterId();

    const consultationData = {
      appointmentId: selectedAppointment._id,
      patientType,
      date: dayjs(visitDate).format("DD-MM-YYYY"),
      time: dayjs(visitTime).format("HH:mm"),
      observations,
      complaints,
      diagnosis,
      notes,
      advices,
      treatments: billableTreatmentNames(),
      ...buildPackageFields(resolvedMasterId),
      prescribedMedicines: medicines.map((m) => ({
        medicineId: m._id || m.medicineId,
        name: m.name,
        dosage: m.dosage,
      })),
      followUpDate: formatFollowUpDate(followUpDate),
    };

    try {
      setConsultationLoading(true);
      await axios.post(
        `${process.env.REACT_APP_API_URL}add-consultation`,
        consultationData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      resetConsultationForm();
      setShowConsultationModal(false);
      fetchAppointments();
      toast.success("Visit added successfully!");
    } catch (error) {
      console.error("Error saving visit:", error);
      handleApiError(error, "Failed to save visit");
    } finally {
      setConsultationLoading(false);
    }
  };

  //New treatments by API
  useEffect(() => {
    const fetchTreatments = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}get-all-services`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );
        setTreatmentOptions(response.data.result);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching treatments:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTreatments();
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  //Medicine part
  const [availableMedicines, setAvailableMedicines] = useState([]);
  const [medicineId, setMedicineId] = useState("");
  const [medicineDosage, setMedicineDosage] = useState("");

  // Add these state variables
  const [medicineSearchTerm, setMedicineSearchTerm] = useState("");
  const [showMedicineDropdown, setShowMedicineDropdown] = useState(false);

  // Add this computed value for filtered medicines
  const filteredMedicines = useMemo(() => {
    if (!medicineSearchTerm) return availableMedicines;
    return availableMedicines.filter((med) =>
      med.name.toLowerCase().includes(medicineSearchTerm.toLowerCase())
    );
  }, [medicineSearchTerm, availableMedicines]);

  // Update your addMedicine function to clear the search
  const addMedicine = () => {
    if (medicineId && medicineDosage) {
      const selectedMedicine = availableMedicines.find(
        (med) => med._id === medicineId
      );
      if (selectedMedicine) {
        setMedicines([
          ...medicines,
          {
            ...selectedMedicine,
            dosage: parseInt(medicineDosage),
          },
        ]);

        // Clear the form
        setMedicineId("");
        setMedicineDosage("");
        setMedicineSearchTerm("");
        setShowMedicineDropdown(false);
      }
    }
  };

  useEffect(() => {
    const fetchMedicines = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}get-all-medicines`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setAvailableMedicines(res.data.result || []);
      } catch (error) {
        console.error("Failed to fetch medicines:", error);
      }
    };

    fetchMedicines();
  }, []);

  // ✅ Early returns must come after all hooks
  if (isLoading) return <SkeletonLoader variant="appointment-card" count={3} />;
  if (error) return <div>Error loading Appointments: {error}</div>;

  //Edit consultation
  const handleEditConsultation = async (appointment) => {
    if (!appointment._id) return;

    setSelectedAppointment(appointment);
    setModalMode("edit");
    setConsultationLoading(true);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}consultations/${appointment._id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const consultationData = response.data.consultation;

      // Populate form with fetched consultation data
      setBp(consultationData.observations?.bp || "");
      setBsl(consultationData.observations?.bsl || "");
      setWeight(consultationData.observations?.weight || "");
      setTemperature(consultationData.observations?.temperature || "");
      setPulse(consultationData.observations?.pulse || "");

      setDiagnosis(consultationData.diagnosis || "");
      setNotes(consultationData.notes || "");
      setPatientType(consultationData.patientType || "");
      setTreatments(
        consultationData.treatments?.map((tName) => {
          const match = treatmentOptions.find((opt) => opt.name === tName);
          return match ? { _id: match._id, name: match.name } : { name: tName };
        }) || []
      );
      setMedicines(consultationData.prescribedMedicines || []);

      if (consultationData.followUpDate) {
        const dateOnly = consultationData.followUpDate.split("T")[0];
        setFollowUpDate(dayjs(dateOnly));
      } else {
        setFollowUpDate(null);
      }
      setShowConsultationModal(true);
    } catch (error) {
      console.error("Error fetching consultation:", error);
      // Treat all errors as empty consultation history (like IPD list)
      // Don't show error toast for empty cases
      resetConsultationForm();
      setShowConsultationModal(true);
    } finally {
      setConsultationLoading(false);
    }
  };

  const handleUpdateConsultation = async () => {
    if (!patientType) {
      toast.error("Patient type is required.");
      return;
    }

    if (!hasConsultationContent()) {
      toast.error("At least one field must be filled before updating.");
      return;
    }

    if (!validatePackage()) return;

    const formatFollowUpDate = (dateValue) => {
      if (!dateValue) return "";
      return dayjs(dateValue).format("DD-MM-YYYY");
    };

    // Check if menstrual history has any data
    const hasMenstrualData = menarche || lmp || bleedingDuration || cycleDuration || cycleRegularity || flow || clots || dysmenorrhea || menopause;

    const observations = {
      bsl,
      co,
      ho,
      kco,
      sh,
      dh,
      addictions,
      oe: {
        bp,
        pulse,
        temp: temperature,
        weight,
        height,
        spo2,
      },
      se: {
        cvs,
        cns,
        rs,
        pa,
      },
      examination: {
        mala,
        mutra,
        jivha,
        nidra,
      },
    };

    // Only include menstrualHistory if at least one field has data
    if (hasMenstrualData) {
      observations.menstrualHistory = {
        menarche,
        lmp: lmp ? dayjs(lmp).format("DD-MM-YYYY") : "",
        bleedingDuration,
        cycleDuration,
        cycleRegularity,
        flow,
        clots,
        dysmenorrhea,
        menopause,
      };
    }

    const resolvedMasterId = resolvePackageMasterId();

    const consultationData = {
      appointmentId: selectedAppointment._id,
      patientType,
      visitId,
      date: dayjs(visitDate).format("DD-MM-YYYY"),
      time: dayjs(visitTime).format("HH:mm"),
      observations,
      complaints,
      diagnosis,
      notes,
      advices,
      treatments: billableTreatmentNames(),
      ...buildPackageFields(resolvedMasterId),
      prescribedMedicines: medicines.map((m) => ({
        medicineId: m._id || m.medicineId,
        name: m.name,
        dosage: m.dosage,
      })),
      followUpDate: formatFollowUpDate(followUpDate),
    };

    try {
      setConsultationLoading(true);
      await axios.put(
        `${process.env.REACT_APP_API_URL}edit-consultation`,
        consultationData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      resetConsultationForm();
      setShowConsultationModal(false);
      fetchAppointments();
      toast.success("Visit updated successfully!");
    } catch (error) {
      console.error("Error updating visit:", error);
      handleApiError(error, "Failed to update visit");
    } finally {
      setConsultationLoading(false);
    }
  };

  const removeTreatment = (index) => {
    setTreatments((prev) => prev.filter((_, i) => i !== index));
  };

  // const removeMedicine = (index) => {
  //   setMedicines((prev) => prev.filter((_, i) => i !== index));
  // };
  const removeMedicine = (index) => {
    const updatedMedicines = medicines.filter((_, i) => i !== index);
    setMedicines(updatedMedicines);
  };

  const continuedPackage = appointmentPackages.find((pkg) => pkg.duration === "multi");
  const earlierPackageItems = continuePackage
    ? resolvePackageItems(itemsFromEarlierVisits(continuedPackage, visitId), treatmentOptions)
    : [];
  const isPackageTreatment = (opt) =>
    continuePackage &&
    (
      earlierPackageItems.some((item) => sameId(item._id, opt._id) || item.name === opt.name) ||
      packageItems.some((item) => sameId(item._id, opt._id) || item.name === opt.name)
    );
  const packagePriceLabel = (item) => {
    if (!continuedPackage || continuedPackage.kind === "custom") return "Included · ₹0";
    const charged =
      sameId(item._id, continuedPackage.masterServiceId) ||
      item.name === continuedPackage.masterName;
    return charged ? `Charged on first visit · ₹${item.cost ?? 0}` : "Included · ₹0";
  };

  return (
    <div className="ba-page-container">
      {appointmentsLoading && <SkeletonLoader variant="overlay" />}

      {showSuccessPopup && (
        <div className="ba-popup-success">Appointment booked successfully!</div>
      )}

      <div className="ba-header">
        <h2 className="ba-title">Book Appointment</h2>
        <button
          className={`ba-button ${
            showForm ? "ba-button-cancel" : "ba-button-add"
          }`}
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? "Cancel" : "Add Appointment"}
        </button>
      </div>

      {showForm && (
        <div className="ba-form-wrapper">
          <div className="ba-form">
            <div className="ba-form-row">
              <div className="ba-input-group">
                <label className="ba-label">Date</label>
                <CustomDatePicker
                  label="Select Date"
                  value={date}
                  onChange={setDate}
                />
              </div>
              <div className="ba-input-group">
                <label className="ba-label">Time</label>
                <CustomTimePicker
                  label="Select Time"
                  value={time}
                  onChange={setTime}
                />
              </div>
            </div>

            <div className="ba-input-group">
              <label className="ba-label">Additional Information (Optional)</label>
              <textarea
                className="ba-textarea"
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                placeholder="Enter appointment details"
              />
            </div>

            <div className="ba-form-actions">
              <button
                className="ba-button ba-button-primary"
                onClick={handleSaveAppointment}
                disabled={savingAppointment}
              >
                {savingAppointment ? "Saving Appointment..." : "Save Appointment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {appointments.length > 0 ? (
        <div className="ba-table-wrapper">
          <table className="ba-appointments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Doctor</th>
                <th>Notes</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map((appt) => (
                <tr key={appt._id}>
                  <td>{formatDate(appt.date)}</td>
                  <td>{appt.doctor?.name || "N/A"}</td>
                  <td>{appt.notes || "N/A"}</td>
                  <td>
                    {appt.consultationId ? (
                      <button
                        className="ba-button ba-button-view"
                        onClick={() => handleManageVisits(appt)}
                        disabled={appt.isInvoiceGenerated === 1}
                        title={
                          appt.isInvoiceGenerated === 1
                            ? "Invoice already generated – viewing disabled"
                            : ""
                        }
                      >
                        <FaEye className="button-icon mr-1" /> Manage Visits
                      </button>
                    ) : (
                      <button
                        className="ba-button ba-button-secondary"
                        onClick={() => handleAddConsultation(appt)}
                      >
                        <FaPlus className="ba-icon" /> Add Visit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="ba-empty">
          
          <h3 className="ba-empty-title">No appointments yet</h3>
          <p className="ba-empty-desc">
            Add your first appointment by clicking the "Add Appointment" button
            above.
          </p>
        </div>
      )}

      {/* Consultation Modal */}
      {showConsultationModal && (
        <div className="ba-modal-overlay">
          <div className="ba-modal">
            <div className="ba-modal-header">
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                {modalMode === "edit" || modalMode === "add" ? (
                  <button
                    className="ba-modal-back"
                    onClick={() => {
                      setModalMode("visits");
                      resetConsultationForm();
                    }}
                    title="Back to visits list"
                  >
                    ← Back
                  </button>
                ) : null}
                <h3>
                  {modalMode === "visits"
                    ? "Manage Visits"
                    : modalMode === "add"
                    ? "Add Visit"
                    : "Edit Visit"}
                </h3>
              </div>
              <button
                className="ba-modal-close"
                onClick={() => {
                  setShowConsultationModal(false);
                  resetConsultationForm();
                }}
              >
                <FaTimes />
              </button>
            </div>

            <div className="ba-modal-body">
              {modalMode === "visits" && (
                <div className="ba-visits-section">
                  <div className="ba-visits-list">
                    {selectedVisits.length > 0 ? (
                      <>
                        {selectedVisits.map((visit, idx) => (
                          <div key={visit._id} className="ba-visit-row">
                            <div className="ba-visit-info">
                              <span className="ba-visit-date">
                                {dayjs(visit.date).format("DD MMM YYYY")} at {visit.time}
                              </span>
                              <span className="ba-visit-doctor">
                                {typeof visit.doctor === "object" ? visit.doctor.name : visit.doctor}
                              </span>
                              {visit.complaints && (
                                <span className="ba-visit-complaints">{visit.complaints}</span>
                              )}
                              {(() => {
                                const pkg = appointmentPackages.find((item) =>
                                  String(item.billingVisitId) === String(visit._id) ||
                                  (item.sessions || []).some((session) => String(session.visitId) === String(visit._id))
                                );
                                if (!pkg) return null;
                                const names = (pkg.items || []).map((item) => item.name).filter(Boolean);
                                return (
                                  <span className="ba-visit-complaints">
                                    {pkg.duration === "multi" ? "Multi-day package" : "Package visit"}
                                    {names.length ? ` · ${names.join(", ")}` : ""}
                                  </span>
                                );
                              })()}
                            </div>
                            <div className="ba-visit-actions">
                              <button
                                className="ba-button ba-button-edit"
                                onClick={() => handleEditVisit(visit)}
                              >
                                <FaEdit /> Edit
                              </button>
                            </div>
                          </div>
                        ))}
                      </>
                    ) : (
                      <div style={{ textAlign: "center", padding: "2rem", color: "#94a3b8" }}>
                        <p>No visits recorded yet</p>
                      </div>
                    )}
                  </div>
                  <button
                    className="ba-button ba-button-primary"
                    onClick={() => {
                      setModalMode("add");
                      setVisitDate(dayjs());
                      setVisitTime(dayjs());
                      resetConsultationForm();
                      const existingPackage = appointmentPackages[0];
                      if (existingPackage?.duration === "multi") {
                        setContinuePackage(true);
                        setPackageLocked(true);
                      } else if (existingPackage) {
                        setPackageLocked(true);
                      }
                    }}
                    style={{ marginTop: "1rem", width: "100%" }}
                  >
                    <FaPlus /> Add New Visit
                  </button>
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <>
                  <div className="ba-input-group">
                    <CustomDropdown
                      label="Patient Type"
                      name="patientType"
                      value={patientType}
                      onChange={(e) => setPatientType(e.target.value)}
                      options={[
                        { label: "Select patient type", value: "" },
                        { label: "Regular", value: "regular" },
                        { label: "Demo", value: "demo" },
                        { label: "New", value: "new" },
                      ]}
                    />
                  </div>

                  <div className="ba-section">
                    <h3 className="ba-section-title">Visit Date & Time</h3>
                    <div className="ba-form-row">
                      <div className="ba-input-group">
                        <label className="ba-label">Date</label>
                        <CustomDatePicker
                          label="Select Date"
                          value={visitDate}
                          onChange={setVisitDate}
                        />
                      </div>
                      <div className="ba-input-group">
                        <label className="ba-label">Time</label>
                        <CustomTimePicker
                          label="Select Time"
                          value={visitTime}
                          onChange={setVisitTime}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="ba-input-group">
                    <label className="ba-label">Complaints</label>
                    <textarea
                      className="ba-textarea"
                      placeholder="Describe patient complaints"
                      value={complaints}
                      onChange={(e) => setComplaints(e.target.value)}
                    />
                  </div>
                </>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Vital Observations</h3>
                <div className="ba-form-row">
                  <div className="ba-input-group">
                    <label className="ba-label">BP</label>
                    <input
                      type="text"
                      className="ba-input"
                      placeholder="e.g. 120/80"
                      value={bp}
                      onChange={(e) => setBp(e.target.value)}
                      readOnly={modalMode === "view"}
                    />
                  </div>
                  <div className="ba-input-group">
                    <label className="ba-label">BSL</label>
                    <input
                      type="number"
                      className="ba-input"
                      placeholder="Blood sugar level"
                      value={bsl}
                      min="0"
                      onChange={(e) => setBsl(e.target.value)}
                      readOnly={modalMode === "view"}
                    />
                  </div>
                </div>
                <div className="ba-form-row">
                  <div className="ba-input-group">
                    <label className="ba-label">Weight</label>
                    <input
                      type="number"
                      className="ba-input"
                      placeholder="e.g. 70 kg"
                      value={weight}
                      min="0"
                      onChange={(e) => setWeight(e.target.value)}
                      readOnly={modalMode === "view"}
                    />
                  </div>
                  <div className="ba-input-group">
                    <label className="ba-label">Temperature</label>
                    <input
                      type="number"
                      className="ba-input"
                      placeholder="e.g. 98.6 F"
                      value={temperature}
                      min="0"
                      step="0.1"
                      onChange={(e) => setTemperature(e.target.value)}
                      readOnly={modalMode === "view"}
                    />
                  </div>
                </div>
                <div className="ba-form-row">
                  <div className="ba-input-group">
                    <label className="ba-label">Pulse</label>
                    <input
                      type="number"
                      className="ba-input"
                      placeholder="Pulse"
                      value={pulse}
                      min="0"
                      onChange={(e) => setPulse(e.target.value)}
                      readOnly={modalMode === "view"}
                    />
                  </div>
                  <div className="ba-input-group">
                    <label className="ba-label">Height</label>
                    <input
                      type="text"
                      className="ba-input"
                      placeholder="e.g. 170 cm"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      readOnly={modalMode === "view"}
                    />
                  </div>
                </div>
                <div className="ba-form-row">
                  <div className="ba-input-group">
                    <label className="ba-label">SpO2 (Oxygen Saturation)</label>
                    <input
                      type="text"
                      className="ba-input"
                      placeholder="e.g. 98%"
                      value={spo2}
                      onChange={(e) => setSpo2(e.target.value)}
                      readOnly={modalMode === "view"}
                    />
                  </div>
                  <div className="ba-input-group">
                    <label className="ba-label">Follow-up Date</label>
                    <CustomDatePicker
                      label="Select Follow-up Date"
                      value={followUpDate}
                      onChange={setFollowUpDate}
                      disabled={modalMode === "view"}
                      minDate={dayjs().startOf("day")}
                    />
                  </div>
                </div>
              </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Chief Complaint (C/O)</h3>
                  <textarea
                    className="ba-textarea"
                    placeholder="Chief complaint"
                    value={co}
                    onChange={(e) => setCo(e.target.value)}
                  />
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">History of Presenting Illness (H/O)</h3>
                  <textarea
                    className="ba-textarea"
                    placeholder="History of presenting illness"
                    value={ho}
                    onChange={(e) => setHo(e.target.value)}
                  />
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Known Case Of (K/C/O)</h3>
                  <textarea
                    className="ba-textarea"
                    placeholder="Known case of (existing conditions)"
                    value={kco}
                    onChange={(e) => setKco(e.target.value)}
                  />
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Social History (S/H)</h3>
                  <textarea
                    className="ba-textarea"
                    placeholder="Social history (smoking, drinking, occupation, etc.)"
                    value={sh}
                    onChange={(e) => setSh(e.target.value)}
                  />
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Systemic Examination (S/E)</h3>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <label className="ba-label">CVS (Cardiovascular System)</label>
                      <textarea
                        className="ba-textarea"
                        placeholder="CVS examination findings"
                        value={cvs}
                        onChange={(e) => setCvs(e.target.value)}
                        style={{ minHeight: '80px' }}
                      />
                    </div>
                    <div className="ba-input-group">
                      <label className="ba-label">CNS (Central Nervous System)</label>
                      <textarea
                        className="ba-textarea"
                        placeholder="CNS examination findings"
                        value={cns}
                        onChange={(e) => setCns(e.target.value)}
                        style={{ minHeight: '80px' }}
                      />
                    </div>
                  </div>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <label className="ba-label">RS (Respiratory System)</label>
                      <textarea
                        className="ba-textarea"
                        placeholder="RS examination findings"
                        value={rs}
                        onChange={(e) => setRs(e.target.value)}
                        style={{ minHeight: '80px' }}
                      />
                    </div>
                    <div className="ba-input-group">
                      <label className="ba-label">P/A (Per Abdomen)</label>
                      <textarea
                        className="ba-textarea"
                        placeholder="Abdominal examination findings"
                        value={pa}
                        onChange={(e) => setPa(e.target.value)}
                        style={{ minHeight: '80px' }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Ayurvedic Examination</h3>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <label className="ba-label">Mala (Bowel Habits)</label>
                      <input
                        type="text"
                        className="ba-input"
                        placeholder="e.g. Sama, Sama, etc."
                        value={mala}
                        onChange={(e) => setMala(e.target.value)}
                      />
                    </div>
                    <div className="ba-input-group">
                      <label className="ba-label">Mutra (Urine Characteristics)</label>
                      <input
                        type="text"
                        className="ba-input"
                        placeholder="e.g. Prakruta"
                        value={mutra}
                        onChange={(e) => setMutra(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <label className="ba-label">Jivha (Tongue)</label>
                      <input
                        type="text"
                        className="ba-input"
                        placeholder="e.g. Sama, Nirama"
                        value={jivha}
                        onChange={(e) => setJivha(e.target.value)}
                      />
                    </div>
                    <div className="ba-input-group">
                      <label className="ba-label">Nidra (Sleep)</label>
                      <input
                        type="text"
                        className="ba-input"
                        placeholder="e.g. Normal, Reduced, Excessive"
                        value={nidra}
                        onChange={(e) => setNidra(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Dietary History (D/H)</h3>
                  <textarea
                    className="ba-textarea"
                    placeholder="Dietary habits and food preferences"
                    value={dh}
                    onChange={(e) => setDh(e.target.value)}
                  />
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Addictions (if any)</h3>
                  <textarea
                    className="ba-textarea"
                    placeholder="Tobacco, alcohol, drugs, etc."
                    value={addictions}
                    onChange={(e) => setAddictions(e.target.value)}
                  />
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-section">
                  <h3 className="ba-section-title">Menstrual History (For Females)</h3>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <label className="ba-label">Menarche (Age at First Menstruation)</label>
                      <input
                        type="text"
                        className="ba-input"
                        placeholder="e.g. 12 years"
                        value={menarche}
                        onChange={(e) => setMenarche(e.target.value)}
                      />
                    </div>
                    <div className="ba-input-group">
                      <label className="ba-label">LMP (Last Menstrual Period)</label>
                      <CustomDatePicker
                        label="Select LMP Date"
                        value={lmp}
                        onChange={setLmp}
                      />
                    </div>
                  </div>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <label className="ba-label">Bleeding Duration (Days)</label>
                      <input
                        type="text"
                        className="ba-input"
                        placeholder="e.g. 3-5 days"
                        value={bleedingDuration}
                        onChange={(e) => setBleedingDuration(e.target.value)}
                      />
                    </div>
                    <div className="ba-input-group">
                      <label className="ba-label">Cycle Duration (Days)</label>
                      <input
                        type="text"
                        className="ba-input"
                        placeholder="e.g. 28 days"
                        value={cycleDuration}
                        onChange={(e) => setCycleDuration(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <CustomDropdown
                        label="Cycle Regularity"
                        value={cycleRegularity}
                        name="cycleRegularity"
                        onChange={(e) => setCycleRegularity(e.target.value)}
                        options={[
                          { label: "Select...", value: "" },
                          { label: "Regular", value: "Regular" },
                          { label: "Irregular", value: "Irregular" },
                        ]}
                      />
                    </div>
                    <div className="ba-input-group">
                      <CustomDropdown
                        label="Flow Quantity"
                        value={flow}
                        name="flow"
                        onChange={(e) => setFlow(e.target.value)}
                        options={[
                          { label: "Select...", value: "" },
                          { label: "Scanty", value: "Scanty" },
                          { label: "Moderate", value: "Moderate" },
                          { label: "Heavy", value: "Heavy" },
                        ]}
                      />
                    </div>
                  </div>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <CustomDropdown
                        label="Clots"
                        value={clots}
                        name="clots"
                        onChange={(e) => setClots(e.target.value)}
                        options={[
                          { label: "Select...", value: "" },
                          { label: "Present", value: "Present" },
                          { label: "Absent", value: "Absent" },
                        ]}
                      />
                    </div>
                    <div className="ba-input-group">
                      <CustomDropdown
                        label="Dysmenorrhea (Period Pain)"
                        value={dysmenorrhea}
                        name="dysmenorrhea"
                        onChange={(e) => setDysmenorrhea(e.target.value)}
                        options={[
                          { label: "Select...", value: "" },
                          { label: "Mild", value: "Mild" },
                          { label: "Moderate", value: "Moderate" },
                          { label: "Severe", value: "Severe" },
                        ]}
                      />
                    </div>
                  </div>
                  <div className="ba-form-row">
                    <div className="ba-input-group">
                      <CustomDropdown
                        label="Menopause"
                        value={menopause}
                        name="menopause"
                        onChange={(e) => setMenopause(e.target.value)}
                        options={[
                          { label: "Select...", value: "" },
                          { label: "Yes", value: "Yes" },
                          { label: "No", value: "No" },
                        ]}
                      />
                    </div>
                  </div>
                </div>
              )}

              {(modalMode === "add" || modalMode === "edit") && (
                <>
              <div className="ba-input-group">
                <label className="ba-label">Diagnosis</label>
                <textarea
                  className="ba-textarea"
                  placeholder="Describe the diagnosis provided"
                  value={diagnosis}
                  onChange={(e) => setDiagnosis(e.target.value)}
                />
              </div>

              <div className="ba-input-group">
                <label className="ba-label">Notes</label>
                <textarea
                  className="ba-textarea"
                  placeholder="Write notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  readOnly={modalMode === "view"}
                />
              </div>

              <div className="ba-input-group">
                <label className="ba-label">Advices</label>
                <textarea
                  className="ba-textarea"
                  placeholder="Medical advice and recommendations"
                  value={advices}
                  onChange={(e) => setAdvices(e.target.value)}
                  readOnly={modalMode === "view"}
                />
              </div>

              <div className="ba-section">
                <h3 className="ba-section-title">Treatments</h3>
                <div className="ba-dropdown-wrapper" ref={treatmentDropdownRef}>
                  <button
                    className="ba-dropdown-toggle"
                    onClick={toggleDropdown}
                    disabled={modalMode === "view"}
                  >
                    <span>Select Treatments</span>
                    <span
                      className={`ba-dropdown-icon ${
                        isDropdownOpen ? "open" : ""
                      }`}
                    >
                      <FaChevronDown />
                    </span>
                  </button>

                  {isDropdownOpen && (
                    <div className="ba-dropdown-menu">
                      <input
                        type="text"
                        className="ba-input ba-search-input"
                        placeholder="Search treatments..."
                        value={treatmentSearch}
                        onChange={(e) => setTreatmentSearch(e.target.value)}
                      />
                      <div className="ba-checkbox-list">
                        {treatmentOptions
                          .filter((opt) =>
                            opt.name
                              .toLowerCase()
                              .includes(treatmentSearch.toLowerCase()) &&
                            !isPackageTreatment(opt)
                          )
                          .map((opt) => (
                            <label key={opt._id} className="ba-checkbox-item">
                              <input
                                type="checkbox"
                                checked={treatments.some(
                                  (t) => t._id === opt._id
                                )}
                                onChange={() => handleTreatmentToggle(opt)}
                                disabled={modalMode === "view"}
                              />
                              {/* <span className="ba-checkbox-custom" /> */}
                              <span className="ba-checkbox-label">
                                {opt.name} (₹{opt.cost})
                              </span>
                            </label>
                          ))}
                        {treatmentOptions.filter((opt) =>
                          opt.name
                            .toLowerCase()
                            .includes(treatmentSearch.toLowerCase()) &&
                          !isPackageTreatment(opt)
                        ).length === 0 && (
                          <div className="ba-no-results">No results found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {treatments.length > 0 && (
                  <div className="ba-treat-container">
                    <h4 className="ba-treat-title">Selected Treatments</h4>
                    <ul className="ba-treat-list">
                      {treatments.map((treatment, index) => (
                        <li key={index} className="ba-treat-item">
                          <span className="ba-treat-name">
                            {treatment.name} (₹{treatment.cost ?? treatmentOptions.find((opt) => sameId(opt._id, treatment._id))?.cost ?? 0})
                          </span>
                          {modalMode !== "view" && (
                            <button
                              className="ba-treat-remove-btn"
                              onClick={() => removeTreatment(index)}
                              title="Remove treatment"
                            >
                              <FaTrashAlt />
                            </button>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="ba-package-box">
                  <div className="ba-package-head">
                    <h4 className="ba-treat-title">Package billing</h4>
                    <p className="ba-package-note">
                      Bill a standard package, or a custom one such as 10 days Panchkarma where the doctor sets the name, days, and price.
                    </p>
                  </div>
                  {packageLocked ? (
                    continuePackage ? (
                      <div className="ba-package-continue">
                        <p className="ba-package-locked">
                          {continuedPackage?.kind === "custom"
                            ? `This visit continues ${continuedPackage?.packageName || "the custom package"}. The price was charged on the first visit. Treatments already in the package are listed below, and a treatment given on this visit can be added at ₹0.`
                            : `This visit continues the package. ${continuedPackage?.masterName || "The charged treatment"} keeps the price from the first visit. Treatments already in the package are listed below, and a treatment given on this visit can be added at ₹0.`}
                        </p>
                        <p className="ba-package-step-title">Already in this package</p>
                        {earlierPackageItems.length > 0 ? (
                          <div className="ba-package-list">
                            {earlierPackageItems.map((item) => {
                              const charged =
                                continuedPackage?.kind !== "custom" &&
                                (sameId(item._id, continuedPackage?.masterServiceId) ||
                                  item.name === continuedPackage?.masterName);
                              return (
                                <div
                                  key={`earlier-${item._id}`}
                                  className={`ba-package-card selected static ${charged ? "master" : ""}`}
                                >
                                  <span className="ba-package-tick" aria-hidden="true" />
                                  <span className="ba-package-card-copy">
                                    <strong>{item.name}</strong>
                                    <small>{packagePriceLabel(item)}</small>
                                  </span>
                                  <span className="ba-package-badge">Earlier visit</span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="ba-package-note">No treatments have been saved on this package yet.</p>
                        )}
                        <p className="ba-package-step-title">Add a treatment on this visit</p>
                        <p className="ba-package-note">
                          Tick a treatment given today. It joins the same package and stays at ₹0.
                        </p>
                        <div className="ba-package-list">
                          {treatmentOptions
                            .filter((opt) => !earlierPackageItems.some((item) => sameId(item._id, opt._id) || item.name === opt.name))
                            .map((opt) => {
                              const selected = packageItems.some((item) => sameId(item._id, opt._id));
                              return (
                                <div
                                  key={`add-${opt._id}`}
                                  className={`ba-package-card ${selected ? "selected" : ""}`}
                                >
                                  <label>
                                    <input
                                      type="checkbox"
                                      checked={selected}
                                      onChange={() => togglePackageItem(opt)}
                                    />
                                    <span className="ba-package-tick" aria-hidden="true" />
                                    <span className="ba-package-card-copy">
                                      <strong>{opt.name}</strong>
                                      <small>{selected ? "Added on this visit · ₹0" : `Listed ₹${opt.cost ?? 0}`}</small>
                                    </span>
                                  </label>
                                </div>
                              );
                            })}
                        </div>
                        <p className="ba-package-summary">
                          {earlierPackageItems.length} from earlier visits
                          {packageItems.length ? ` · ${packageItems.length} added on this visit` : ""}
                        </p>
                      </div>
                    ) : (
                      <p className="ba-package-locked">
                        This appointment already has a package. Extra treatments added above are billed separately.
                      </p>
                    )
                  ) : (
                    <>
                      <label className={`ba-package-toggle ${packageMode ? "on" : ""}`}>
                        <input
                          type="checkbox"
                          checked={packageMode}
                          onChange={(event) => {
                            const enabled = event.target.checked;
                            setPackageMode(enabled);
                            if (!enabled) {
                              setPackageItems([]);
                              setMasterId("");
                            }
                          }}
                        />
                        <span className="ba-package-switch" aria-hidden="true" />
                        <span className="ba-package-toggle-copy">
                          <strong>Bill as a package</strong>
                          <small>Turn this on, then choose a standard or custom package.</small>
                        </span>
                      </label>
                      {packageMode && (
                        <div className="ba-package-panel">
                          <div className="ba-package-step">
                            <span className="ba-package-step-num">1</span>
                            <div className="ba-package-step-body">
                              <p className="ba-package-step-title">What kind of package is this?</p>
                              <div className="ba-package-kind">
                                <label className={packageKind === "standard" ? "active" : ""}>
                                  <input
                                    type="radio"
                                    name="packageKind"
                                    checked={packageKind === "standard"}
                                    onChange={() => setPackageKind("standard")}
                                  />
                                  <strong>Standard package</strong>
                                  <small>One treatment keeps its listed price. The rest are ₹0.</small>
                                </label>
                                <label className={packageKind === "custom" ? "active" : ""}>
                                  <input
                                    type="radio"
                                    name="packageKind"
                                    checked={packageKind === "custom"}
                                    onChange={() => setPackageKind("custom")}
                                  />
                                  <strong>Custom package</strong>
                                  <small>Doctor sets the name, days, and price. Example: 10 days Panchkarma.</small>
                                </label>
                              </div>
                            </div>
                          </div>

                          {packageKind === "standard" ? (
                            <div className="ba-package-step">
                              <span className="ba-package-step-num">2</span>
                              <div className="ba-package-step-body">
                                <p className="ba-package-step-title">How long is this package?</p>
                                <div className="ba-package-duration">
                                  <label className={packageDuration === "single" ? "active" : ""}>
                                    <input
                                      type="radio"
                                      name="packageDuration"
                                      checked={packageDuration === "single"}
                                      onChange={() => setPackageDuration("single")}
                                    />
                                    <strong>Single day</strong>
                                    <small>Finished in one visit</small>
                                  </label>
                                  <label className={packageDuration === "multi" ? "active" : ""}>
                                    <input
                                      type="radio"
                                      name="packageDuration"
                                      checked={packageDuration === "multi"}
                                      onChange={() => setPackageDuration("multi")}
                                    />
                                    <strong>Multiple days</strong>
                                    <small>Continues across visits</small>
                                  </label>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="ba-package-step">
                              <span className="ba-package-step-num">2</span>
                              <div className="ba-package-step-body">
                                <p className="ba-package-step-title">Name the package and set the price</p>
                                <p className="ba-package-note">
                                  The price below is the full charge. Included treatments are not billed on their own.
                                </p>
                                <div className="ba-package-custom">
                                  <div className="ba-package-field">
                                    <label htmlFor="custom-package-name">Package name</label>
                                    <input
                                      id="custom-package-name"
                                      type="text"
                                      value={customPackageName}
                                      placeholder="Panchkarma"
                                      onChange={(event) => setCustomPackageName(event.target.value)}
                                    />
                                  </div>
                                  <div className="ba-package-field">
                                    <label htmlFor="custom-package-days">Days</label>
                                    <input
                                      id="custom-package-days"
                                      type="number"
                                      min="1"
                                      inputMode="numeric"
                                      value={customPackageDays}
                                      placeholder="10"
                                      onChange={(event) => setCustomPackageDays(event.target.value.replace(/[^\d]/g, ""))}
                                    />
                                  </div>
                                  <div className="ba-package-field">
                                    <label htmlFor="custom-package-price">Price</label>
                                    <div className="ba-package-price">
                                      <span aria-hidden="true">₹</span>
                                      <input
                                        id="custom-package-price"
                                        type="number"
                                        min="0"
                                        inputMode="decimal"
                                        value={customPackagePrice}
                                        placeholder="15000"
                                        onChange={(event) => setCustomPackagePrice(event.target.value)}
                                      />
                                    </div>
                                  </div>
                                </div>
                                <div className="ba-package-preview">
                                  <strong>
                                    {customPackageDays ? `${customPackageDays} days ` : ""}
                                    {customPackageName.trim() || "Custom package"}
                                  </strong>
                                  <span>
                                    {customPackagePrice !== "" && !Number.isNaN(Number(customPackagePrice))
                                      ? `Billed at ₹${Number(customPackagePrice).toLocaleString("en-IN")}`
                                      : "Set the price the doctor has decided"}
                                    {packageItems.length > 0
                                      ? ` · ${packageItems.length} treatment${packageItems.length === 1 ? "" : "s"} included`
                                      : ""}
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          <div className="ba-package-step">
                            <span className="ba-package-step-num">3</span>
                            <div className="ba-package-step-body">
                              <p className="ba-package-step-title">
                                {packageKind === "custom" ? "Treatments included" : "Choose treatments"}
                              </p>
                              <p className="ba-package-note">
                                {packageKind === "custom"
                                  ? "Tick every treatment that belongs in this package. Each one is included at ₹0."
                                  : <>Tick every treatment in the package, then tap <em>Charge this</em> on the one that should be billed.</>}
                              </p>
                              <div className="ba-package-list">
                                {treatmentOptions.map((opt) => {
                                  const selected = packageItems.some((item) => sameId(item._id, opt._id));
                                  const isMaster = packageKind === "standard" && selected && sameId(masterId, opt._id);
                                  return (
                                    <div
                                      key={`pkg-${opt._id}`}
                                      className={`ba-package-card ${selected ? "selected" : ""} ${isMaster ? "master" : ""}`}
                                    >
                                      <label>
                                        <input
                                          type="checkbox"
                                          checked={selected}
                                          onChange={() => togglePackageItem(opt)}
                                        />
                                        <span className="ba-package-tick" aria-hidden="true" />
                                        <span className="ba-package-card-copy">
                                          <strong>{opt.name}</strong>
                                          <small>
                                            {packageKind === "custom"
                                              ? (selected ? "Included · ₹0" : `Listed ₹${opt.cost ?? 0}`)
                                              : (selected && !isMaster ? "Included · ₹0" : `₹${opt.cost ?? 0}`)}
                                          </small>
                                        </span>
                                      </label>
                                      {packageKind === "standard" && selected && (
                                        <button
                                          type="button"
                                          className={`ba-package-master ${isMaster ? "active" : ""}`}
                                          onClick={() => setMasterId(opt._id)}
                                        >
                                          {isMaster ? "Charged" : "Charge this"}
                                        </button>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                              <p className="ba-package-summary">
                                {packageKind === "custom"
                                  ? (customPackageName.trim() || customPackagePrice
                                    ? `${customPackageDays ? `${customPackageDays} days ` : ""}${customPackageName.trim() || "Custom package"}${
                                        customPackagePrice !== "" && !Number.isNaN(Number(customPackagePrice))
                                          ? ` · ₹${Number(customPackagePrice).toLocaleString("en-IN")}`
                                          : ""
                                      }${packageItems.length ? ` · ${packageItems.length} included` : ""}`
                                    : "Add a name, the number of days, and the price.")
                                  : (packageItems.length === 0
                                    ? "No treatments selected yet."
                                    : `${packageItems.length} in package · ${
                                        packageItems.find((item) => sameId(item._id, masterId))?.name || "Choose one to charge"
                                      } is billed.`)}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Medicines Section (Visible in both 'add' and 'edit' modes) */}
              {(modalMode === "add" || modalMode === "edit") && (
                <div className="ba-med-section">
                  <h3 className="ba-med-title">Medicines</h3>
                  <div className="ba-med-inputs">
                    <div
                      className="ba-med-wrapper"
                      tabIndex={0}
                      onBlur={() => setShowMedicineDropdown(false)}
                      ref={medicineDropdownRef}
                    >
                      <input
                        className="ba-med-input"
                        placeholder="Search and select medicine..."
                        value={medicineSearchTerm}
                        onChange={(e) => {
                          setMedicineSearchTerm(e.target.value);
                          setShowMedicineDropdown(true);
                        }}
                        onFocus={() => setShowMedicineDropdown(true)}
                      />
                      {showMedicineDropdown && (
                        <div className="ba-med-list">
                          {filteredMedicines.length > 0 ? (
                            filteredMedicines.map((med) => (
                              <div
                                key={med._id}
                                className="ba-med-option"
                                onMouseDown={() => {
                                  setMedicineId(med._id);
                                  setMedicineSearchTerm(med.name);
                                  setShowMedicineDropdown(false);
                                }}
                              >
                                <span className="ba-med-name">
                                  {med.name}
                                </span>
                                <span className="ba-med-price">
                                  ₹{med.cost}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="ba-med-no-result">
                              No results found
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <input
                      type="number"
                      className="ba-med-dosage"
                      placeholder="Dosage"
                      value={medicineDosage}
                      min="1"
                      onChange={(e) => setMedicineDosage(e.target.value)}
                    />

                    <button
                      className="ba-med-add-btn"
                      onClick={addMedicine}
                      disabled={!medicineId || !medicineDosage}
                    >
                      Add Medicine
                    </button>
                  </div>
                </div>
              )}

              {medicines.length > 0 && (
                <div className="ba-pres-list-container">
                  <h4 className="ba-pres-title">Prescribed Medicines</h4>
                  <ul className="ba-pres-list">
                    {medicines.map((m, i) => (
                      <li key={i} className="ba-pres-item">
                        <div className="ba-pres-info">
                          <span className="ba-pres-name">{m.name}</span>
                          <span className="ba-pres-dosage">
                            Dosage: {m.dosage}
                          </span>
                          <span className="ba-pres-cost">
                            Total: ₹{parseFloat(m.dosage) * m.cost || m.cost}{" "}
                            <small className="ba-pres-cost-unit">(₹{m.cost} per unit)</small>
                          </span>
                        </div>
                        {(modalMode === "add" || modalMode === "edit") && (
                          <button
                            className="ba-pres-remove-btn"
                            onClick={() => removeMedicine(i)}
                            title="Remove medicine"
                          >
                            <FaTrashAlt />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
                </>
              )}
            </div>

            {modalMode !== "visits" && (
              <div className="ba-modal-footer">
                <button
                  className="ba-button ba-button-cancel"
                  onClick={() => {
                    setShowConsultationModal(false);
                    resetConsultationForm();
                  }}
                >
                  Cancel
                </button>
                {modalMode === "add" && (
                  <button
                    className="ba-button ba-button-primary"
                    onClick={handleSaveConsultation}
                    disabled={consultationLoading}
                  >
                    {consultationLoading ? "Saving..." : "Save Visit"}
                  </button>
                )}
                {modalMode === "edit" && (
                  <button
                    className="ba-button ba-button-primary"
                    onClick={handleUpdateConsultation}
                    disabled={consultationLoading}
                  >
                    {consultationLoading ? "Updating..." : "Update Visit"}
                  </button>
                )}
              </div>
            )}
            {modalMode === "visits" && (
              <div className="ba-modal-footer">
                <button
                  className="ba-button ba-button-cancel"
                  onClick={() => {
                    setShowConsultationModal(false);
                    resetConsultationForm();
                  }}
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BookAppointment;
